/* ==========================================================================
   Remodius — Collection Tabs interactions
   --------------------------------------------------------------------------
   - Pill toggle met sliding thumb tussen actieve tabs
   - Wisselt panel, heading, en view-all label synchroon
   - Add-to-cart submit via fetch (geen page reload), fire 'cart:updated' event
   ========================================================================== */

(function () {
  'use strict';

  function init(section) {
    const buttons = section.querySelectorAll('.rem-coll__toggle-btn');
    const thumb = section.querySelector('.rem-coll__toggle-thumb');
    const panes = section.querySelectorAll('.rem-coll__panel');
    const headings = section.querySelectorAll('.rem-coll__heading-pane');
    const viewalls = section.querySelectorAll('.rem-coll__viewall');

    function moveThumb(target) {
      if (!thumb || !target) return;
      const wrapRect = target.parentElement.getBoundingClientRect();
      const btnRect = target.getBoundingClientRect();
      thumb.style.left = (btnRect.left - wrapRect.left) + 'px';
      thumb.style.width = btnRect.width + 'px';
    }

    function activate(paneId, animate) {
      buttons.forEach(b => {
        const active = b.dataset.pane === paneId;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) moveThumb(b);
      });
      panes.forEach(p => p.classList.toggle('is-active', p.dataset.pane === paneId));
      headings.forEach(h => h.classList.toggle('is-active', h.dataset.pane === paneId));
      viewalls.forEach(v => v.classList.toggle('is-active', v.dataset.pane === paneId));
    }

    buttons.forEach(btn => {
      btn.addEventListener('click', () => activate(btn.dataset.pane, true));
    });

    // Initial thumb position — runs after fonts/layout settle
    const firstActive = section.querySelector('.rem-coll__toggle-btn.is-active');
    if (firstActive) {
      requestAnimationFrame(() => moveThumb(firstActive));
    }

    // Reposition thumb on resize
    let raf;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const active = section.querySelector('.rem-coll__toggle-btn.is-active');
        if (active) moveThumb(active);
      });
    });

    // Carousel nav buttons — scroll grid by ~one card width
    section.querySelectorAll('[data-rem-coll-scroller]').forEach(scroller => {
      const grid = scroller.querySelector('[data-rem-coll-grid]');
      const prev = scroller.querySelector('[data-rem-coll-nav="prev"]');
      const next = scroller.querySelector('[data-rem-coll-nav="next"]');
      if (!grid) return;

      function updateNavState() {
        const max = grid.scrollWidth - grid.clientWidth;
        const atStart = grid.scrollLeft <= 2;
        const atEnd = grid.scrollLeft >= max - 2;
        const hasOverflow = max > 2;
        if (prev) {
          prev.classList.toggle('is-disabled', atStart || !hasOverflow);
          prev.tabIndex = atStart || !hasOverflow ? -1 : 0;
        }
        if (next) {
          next.classList.toggle('is-disabled', atEnd || !hasOverflow);
          next.tabIndex = atEnd || !hasOverflow ? -1 : 0;
        }
      }

      function step(direction) {
        const firstCard = grid.querySelector('.rem-coll-card');
        const cardW = firstCard ? firstCard.getBoundingClientRect().width : grid.clientWidth * 0.6;
        const gap = parseFloat(getComputedStyle(grid).columnGap || getComputedStyle(grid).gap || 20) || 20;
        grid.scrollBy({ left: direction * (cardW + gap), behavior: 'smooth' });
      }

      if (prev) prev.addEventListener('click', () => step(-1));
      if (next) next.addEventListener('click', () => step(1));

      grid.addEventListener('scroll', () => {
        cancelAnimationFrame(scroller._raf);
        scroller._raf = requestAnimationFrame(updateNavState);
      }, { passive: true });

      // Recalculate on resize + after fonts/images settle
      window.addEventListener('resize', () => {
        cancelAnimationFrame(scroller._raf);
        scroller._raf = requestAnimationFrame(updateNavState);
      });
      requestAnimationFrame(updateNavState);
      setTimeout(updateNavState, 300);
    });

    // Re-evaluate nav state when a tab panel becomes visible (the inactive
    // panels report scrollWidth=clientWidth because of display:none).
    section.addEventListener('click', (e) => {
      if (!e.target.closest('.rem-coll__toggle-btn')) return;
      setTimeout(() => {
        section.querySelectorAll('[data-rem-coll-scroller]').forEach(s => {
          const g = s.querySelector('[data-rem-coll-grid]');
          if (g) g.dispatchEvent(new Event('scroll'));
        });
      }, 50);
    });

    // Fetch fresh cart state and broadcast it so the mega-menu counter
    // (and any cart drawers) refresh. Pass the full cart object in
    // event.detail.cart so listeners can read item_count. If the fetch
    // fails we still fire a detail-less refresh event for drawers that
    // self-fetch, but we skip cart:updated to avoid clobbering the
    // counter with 0 on transient network errors.
    async function broadcastCart() {
      let cart = null;
      try {
        const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        if (r.ok) cart = await r.json();
      } catch (_) { /* swallow — handled below */ }

      if (cart) {
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('cart-update',  { bubbles: true, detail: { cart: cart } }));
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
      return cart;
    }

    // Add-to-cart — async, no page reload
    section.querySelectorAll('[data-rem-coll-add]').forEach(form => {
      // Cards wrap their content in <a href="product-url">, so a click on
      // the + button bubbles up and would trigger navigation. Stop it here
      // so only the form submit (handled below) runs.
      form.addEventListener('click', (e) => e.stopPropagation());

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('button');
        if (btn) { btn.disabled = true; btn.style.filter = 'brightness(0.9)'; }
        try {
          const res = await fetch(form.action, {
            method: 'POST',
            headers: {
              'Accept': 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: new FormData(form),
          });
          if (!res.ok) throw new Error('Add to cart failed');
          await broadcastCart();
        } catch (err) {
          // Fallback: submit normally on error
          form.submit();
        } finally {
          if (btn) { btn.disabled = false; btn.style.filter = ''; }
        }
      });
    });

    // Toggle add/remove — bidirectional accessoire switch. State mirrors
    // whether the variant is currently in the cart. Initial sync happens
    // in syncTogglesFromCart() below.
    const toggles = section.querySelectorAll('[data-rem-coll-toggle]');
    toggles.forEach(toggle => {
      // Toggle lives inside the card's wrapping <a>; stop both click and
      // any synthetic bubble so the link doesn't navigate when toggling.
      toggle.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (toggle.disabled) return;

        const variantId = toggle.dataset.variantId;
        if (!variantId) return;

        const wasOn = toggle.classList.contains('is-on');
        toggle.disabled = true;
        // Optimistic flip for snappier feel — revert on failure.
        toggle.classList.toggle('is-on', !wasOn);
        toggle.setAttribute('aria-pressed', !wasOn ? 'true' : 'false');

        try {
          if (!wasOn) {
            const r = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({ id: variantId, quantity: 1 }),
            });
            if (!r.ok) throw new Error('add failed');
          } else {
            // Set this variant's line quantity to 0 — Shopify's /cart/update
            // accepts variant ids as keys, so we don't need the line key.
            const r = await fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({ updates: { [variantId]: 0 } }),
            });
            if (!r.ok) throw new Error('remove failed');
          }
          await broadcastCart();
        } catch (err) {
          // Revert visual state if the network call failed.
          toggle.classList.toggle('is-on', wasOn);
          toggle.setAttribute('aria-pressed', wasOn ? 'true' : 'false');
        } finally {
          toggle.disabled = false;
        }
      });
    });

    // Initial sync: reflect actual cart state on the toggles.
    if (toggles.length) {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(r => r.ok ? r.json() : null)
        .then(cart => {
          if (!cart || !cart.items) return;
          const inCart = new Set(cart.items.map(i => String(i.variant_id)));
          toggles.forEach(t => {
            const on = inCart.has(String(t.dataset.variantId));
            t.classList.toggle('is-on', on);
            t.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
        })
        .catch(() => { /* leave toggles in their server-rendered state */ });
    }
  }

  function boot() {
    document.querySelectorAll('[data-rem-coll]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('[data-rem-coll]');
    if (section) init(section);
  });
})();
